import { useBackendApi } from './backendApi';
import { useObjectDatesMapper } from './Helper';
import { EntityContext } from './llmChatService';
import { Limit, PageResponse } from './Types';

export type LlmMessageRole = 'user' | 'assistant' | 'tool';

export interface LlmConversation {
  id: number;
  title: string;
  model: string | null;
  createdAt: Date;
  updatedAt: Date | null;
  lastMessageAt: Date | null;
  lastMessagePreview: string | null;
}

export type LlmMessageRating = 'positive' | 'negative';

export interface LlmMessage {
  id: number;
  role: LlmMessageRole;
  content: string | null;
  tokenCount: number;
  toolCalls?: unknown[] | null;
  toolCallId?: string | null;
  rating?: LlmMessageRating | null;
  context?: EntityContext[] | null;
  createdAt: Date;
}

export interface UpdateConversationRequest {
  title?: string;
}

export type FeedbackCategory = 'wrong_answer' | 'irrelevant_response' | 'missing_information' | 'other';

export interface CreateFeedbackReportRequest {
  description: string;
  category?: FeedbackCategory | null;
}

export interface FeedbackReport {
  id: number;
  conversationId: number;
  messageId: number;
  description: string;
  category: FeedbackCategory | null;
  createdAt: Date;
}

function useLlmConversationMapper() {
  return useObjectDatesMapper<LlmConversation>([], ['createdAt', 'updatedAt', 'lastMessageAt']);
}

function useLlmMessageMapper() {
  return useObjectDatesMapper<LlmMessage>([], ['createdAt']);
}

function useFeedbackReportMapper() {
  return useObjectDatesMapper<FeedbackReport>([], ['createdAt']);
}

export function useLlmConversationsService() {
  const backendApi = useBackendApi();
  const conversationMapper = useLlmConversationMapper();
  const messageMapper = useLlmMessageMapper();
  const feedbackReportMapper = useFeedbackReportMapper();

  const getConversations = async (limit?: Limit): Promise<PageResponse<LlmConversation>> => {
    const params = limit
      ? `?limit=${limit.limit}&offset=${limit.offset}`
      : '';
    const response = await backendApi.get(`/conversations${params}`) as PageResponse<LlmConversation>;
    return {
      ...response,
      data: conversationMapper.mapList(response.data),
    };
  };

  const getConversation = async (id: number): Promise<LlmConversation> => {
    const response = await backendApi.get(`/conversations/${id}`) as LlmConversation;
    return conversationMapper.mapSingle(response);
  };

  const getConversationMessages = async (conversationId: number): Promise<LlmMessage[]> => {
    const response = await backendApi.get(`/conversations/${conversationId}/messages`) as LlmMessage[];
    return messageMapper.mapList(response);
  };

  const updateConversation = async (
    id: number,
    request: UpdateConversationRequest,
  ): Promise<LlmConversation> => {
    const response = await backendApi.put(`/conversations/${id}`, request) as LlmConversation;
    return conversationMapper.mapSingle(response);
  };

  const deleteConversation = async (id: number): Promise<void> => {
    await backendApi.delete(`/conversations/${id}`);
  };

  const rateMessage = async (
    messageId: number,
    rating: LlmMessageRating | null,
  ): Promise<LlmMessage> => {
    const response = await backendApi.put(
      `/messages/${messageId}/rating`,
      { rating },
    ) as LlmMessage;
    return messageMapper.mapSingle(response);
  };

  const submitFeedback = async (
    conversationId: number,
    request: CreateFeedbackReportRequest,
  ): Promise<FeedbackReport> => {
    const response = await backendApi.post(
      `/conversations/${conversationId}/feedback`,
      request,
    ) as FeedbackReport;
    return feedbackReportMapper.mapSingle(response);
  };

  return {
    getConversations,
    getConversation,
    getConversationMessages,
    updateConversation,
    deleteConversation,
    rateMessage,
    submitFeedback,
  };
}
